const buildSection = ({
  id,
  title,
  icon,
  objective,
  importantFields = [],
  commonErrors = [],
  recommendations = [],
  faqs = [],
  shortcuts = [],
  keywords = [],
}) => ({
  id,
  title,
  icon,
  objective,
  importantFields,
  commonErrors,
  recommendations,
  faqs,
  shortcuts,
  keywords,
});

const buildQuestion = ({ id, question, answer, keywords = [] }) => ({
  id,
  question,
  answer,
  keywords,
});

export const HELP_CONTENT = {
  emission: {
    id: 'emission',
    title: 'Ayuda de emisión de guía',
    tone: 'Guía operativa',
    summary: 'Usá esta pantalla para registrar correctamente una guía antes de emitirla y dejar trazabilidad completa para valorización, despacho y seguimiento.',
    intro: 'La emisión consolida remitente, destinatario, pagador, origen, destino, carga y condición comercial. Si algo queda mal acá, el error se propaga al resto del circuito.',
    sections: [
      buildSection({
        id: 'partes',
        title: 'Cliente, remitente, destinatario y pagador',
        icon: 'Usuarios',
        objective: 'Definí quién entrega, quién recibe y quién asume el costo del envío.',
        importantFields: [
          'Remitente: identifica quién entrega la mercadería y suele determinar condiciones comerciales o de retiro.',
          'Destinatario: define el receptor final y condiciona destino, localidad y validaciones operativas.',
          'Cliente pagador: solo debe cambiarse cuando el costo no lo asume el remitente.',
        ],
        commonErrors: [
          'Cargar remitente y pagador iguales por costumbre sin revisar el acuerdo comercial.',
          'Elegir un destinatario desactualizado o inexistente en vez de confirmar datos vigentes.',
        ],
        recommendations: [
          'Verificá razón social, documento y domicilio antes de seguir a carga.',
          'Si el destinatario no existe, confirmá con administración si corresponde alta previa o gestión eventual controlada.',
        ],
        faqs: [
          '¿Qué cliente tengo que cargar primero?',
          '¿Qué pasa si el destinatario no existe?',
        ],
        keywords: ['cliente', 'remitente', 'destinatario', 'pagador', 'cuenta corriente', 'eventual'],
      }),
      buildSection({
        id: 'origen-destino',
        title: 'Origen, destino, localidad y sucursal operativa',
        icon: 'MapPin',
        objective: 'Asegurá la correcta asignación territorial para despacho, trazabilidad y tarifación.',
        importantFields: [
          'Origen y destino: determinan recorrido, sucursal de intervención y cobertura operativa.',
          'Localidad: impacta en la regla tarifaria, la promesa operativa y la atención por sucursal.',
          'Sucursal operativa: debe representar la unidad que toma la guía para gestionar el envío.',
        ],
        commonErrors: [
          'Elegir una localidad parecida por nombre sin revisar provincia o jurisdicción.',
          'Dejar una sucursal operativa inconsistente con el origen real de toma.',
        ],
        recommendations: [
          'Si hay dudas con la localidad, validá primero cobertura y sucursal responsable.',
          'No uses observaciones para compensar un destino mal cargado; corregí el dato estructurado.',
        ],
        faqs: [
          '¿Por qué importa la localidad?',
          '¿Qué relación tiene con la sucursal operativa?',
        ],
        keywords: ['origen', 'destino', 'localidad', 'sucursal', 'cobertura', 'ruta'],
      }),
      buildSection({
        id: 'carga',
        title: 'Remitos, bultos, kilos y valor declarado',
        icon: 'Package',
        objective: 'Describí la carga con precisión para evitar diferencias comerciales, rechazos y reclamos.',
        importantFields: [
          'Remitos: sirven como respaldo documental y facilitan conciliación con cliente.',
          'Bultos y kilos: alimentan control operativo y valorización.',
          'Valor declarado: solo debe cargarse cuando corresponde cobertura o base comercial adicional.',
        ],
        commonErrors: [
          'Emitir con bultos o kilos estimados cuando el dato real ya está disponible.',
          'Cargar valor declarado en cero cuando el cliente solicita cobertura o seguro.',
        ],
        recommendations: [
          'Si hay varios remitos, revisá que todos correspondan al mismo circuito operativo.',
          'Usá observaciones para aclaraciones puntuales, no para reemplazar datos cuantitativos.',
        ],
        faqs: [
          '¿Cuándo se aplica seguro?',
          '¿Qué pasa si los remitos no coinciden con la carga?',
        ],
        keywords: ['remitos', 'bultos', 'kilos', 'peso', 'valor declarado', 'seguro'],
      }),
      buildSection({
        id: 'comercial',
        title: 'Condición de pago, valorización y emisión final',
        icon: 'Receipt',
        objective: 'Confirmá las condiciones comerciales antes de generar una guía válida.',
        importantFields: [
          'Condición de pago: impacta en facturación y controles de crédito.',
          'Valorización: revisá advertencias o falta de tarifa antes de emitir.',
          'Emisión final: es la última instancia para controlar consistencia general.',
        ],
        commonErrors: [
          'Emitir sin revisar el cliente pagador cuando la condición es cuenta corriente.',
          'Ignorar advertencias de valorización y corregir manualmente después.',
        ],
        recommendations: [
          'Antes de emitir, revisá cliente, destino, peso, valor declarado y condición de pago.',
          'Si la valorización no aplica, no fuerces emisión sin entender el motivo comercial u operativo.',
        ],
        faqs: [
          '¿Qué reviso antes de emitir?',
          '¿Qué hago si la valorización no coincide?',
        ],
        shortcuts: ['F1 abre esta ayuda.', 'F2 vuelve al remitente.', 'F3 salta al destinatario.', 'F5 valida y emite.', 'F7 abre observaciones.', 'Escape cierra el panel de ayuda.'],
        keywords: ['condición de pago', 'valorización', 'emitir', 'tarifa', 'revisión final'],
      }),
    ],
    assistantQuestions: [
      buildQuestion({
        id: 'emission-cliente',
        question: '¿Qué cliente tengo que cargar?',
        answer: 'Primero definí remitente y destinatario. Después revisá si el pagador coincide con el remitente o si el acuerdo comercial exige otro cliente pagador.',
        keywords: ['cliente', 'pagador', 'remitente', 'destinatario'],
      }),
      buildQuestion({
        id: 'emission-destinatario',
        question: '¿Qué pasa si el destinatario no existe?',
        answer: 'No improvises un destinatario parecido. Confirmá si corresponde alta previa o usar un registro eventual controlado, porque el dato impacta en domicilio, trazabilidad y facturación.',
        keywords: ['destinatario', 'alta', 'eventual', 'duplicado'],
      }),
      buildQuestion({
        id: 'emission-localidad',
        question: '¿Por qué importa la localidad?',
        answer: 'Porque define cobertura, sucursal operativa y muchas reglas tarifarias. Una localidad mal elegida puede mandar la guía a una ruta y precio incorrectos.',
        keywords: ['localidad', 'tarifa', 'sucursal', 'cobertura'],
      }),
      buildQuestion({
        id: 'emission-seguro',
        question: '¿Cuándo se aplica seguro?',
        answer: 'Cuando la operación requiere cobertura vinculada al valor declarado o a reglas comerciales específicas. Si hay valor declarado, revisá que el dato sea real y esté completo.',
        keywords: ['seguro', 'valor declarado', 'cobertura'],
      }),
      buildQuestion({
        id: 'emission-check',
        question: '¿Qué reviso antes de emitir?',
        answer: 'Cliente correcto, destino correcto, remitos consistentes, bultos y kilos reales, condición de pago definida y valorización sin advertencias críticas.',
        keywords: ['emitir', 'checklist', 'valorización', 'remitos'],
      }),
    ],
  },
  clients: {
    id: 'clients',
    title: 'Ayuda de clientes',
    tone: 'Gestión administrativa',
    summary: 'Esta pantalla sirve para dar de alta o mantener clientes con datos fiscales, operativos y comerciales consistentes.',
    intro: 'El objetivo es dejar un cliente utilizable por operaciones, cobranzas y administración sin duplicados ni inconsistencias.',
    sections: [
      buildSection({
        id: 'fiscal',
        title: 'Datos fiscales y documento',
        icon: 'FileText',
        objective: 'Registrá correctamente razón social, tipo de documento y condición impositiva.',
        importantFields: [
          'Razón social: debe coincidir con el sujeto fiscal o contractual.',
          'CUIT/DNI: validá que el número corresponda al tipo de documento elegido.',
          'Condición IVA: impacta en facturación y controles administrativos.',
        ],
        commonErrors: [
          'Crear un cliente nuevo con CUIT ya existente por diferencias de escritura.',
          'Usar nombre de fantasía como reemplazo de la razón social.',
        ],
        recommendations: [
          'Antes de guardar, buscá por CUIT, DNI y razón social para evitar duplicados.',
          'Si el dato fiscal está incompleto, no cierres el alta como definitiva.',
        ],
        faqs: [
          '¿Qué datos son obligatorios?',
          '¿Cómo evitar duplicados?',
        ],
        keywords: ['fiscal', 'cuit', 'dni', 'iva', 'duplicado'],
      }),
      buildSection({
        id: 'operativo',
        title: 'Sucursal responsable y operación',
        icon: 'Building2',
        objective: 'Asigná el cliente a la unidad que lo atiende y dejá reglas básicas de operación.',
        importantFields: [
          'Sucursal responsable: indica la referencia operativa principal para gestión y seguimiento.',
          'Estado del cliente: usalo para reflejar si está activo, suspendido o bloqueado.',
          'Motivo del estado: documenta la razón cuando no está activo.',
        ],
        commonErrors: [
          'Dejar la sucursal vacía cuando el cliente ya tiene una atención definida.',
          'Bloquear operativamente sin motivo o sin coordinación comercial.',
        ],
        recommendations: [
          'Si hay bloqueo operativo o comercial, dejá motivo claro y validable.',
          'La sucursal responsable no reemplaza los domicilios del cliente; ambas cosas deben quedar coherentes.',
        ],
        faqs: [
          '¿Qué es sucursal responsable?',
          '¿Qué pasa si hay bloqueo operativo o comercial?',
        ],
        keywords: ['sucursal responsable', 'bloqueo', 'estado', 'operativo', 'comercial'],
      }),
      buildSection({
        id: 'comercial',
        title: 'Condición de pago y categoría de cliente',
        icon: 'Wallet',
        objective: 'Definí cómo se va a operar y cobrar al cliente.',
        importantFields: [
          'Condición de pago: separa contado, cuenta corriente o esquemas mixtos.',
          'Cliente eventual vs cuenta corriente: cambia la forma de operar, controlar crédito y facturar.',
          'Límite de crédito y vencimientos: ayudan a ordenar riesgo comercial.',
        ],
        commonErrors: [
          'Marcar cuenta corriente sin confirmar condiciones crediticias.',
          'Tratar un eventual como cliente habitual sin validar datos mínimos.',
        ],
        recommendations: [
          'Si es eventual, pedí el mínimo dato confiable para evitar reprocesos posteriores.',
          'Si es cuenta corriente, revisá condición de pago, límites y alertas antes de habilitar operación.',
        ],
        faqs: [
          '¿Qué pasa si es eventual?',
          '¿Cuándo corresponde cuenta corriente?',
        ],
        keywords: ['eventual', 'cuenta corriente', 'condición de pago', 'crédito'],
      }),
    ],
    assistantQuestions: [
      buildQuestion({
        id: 'clients-required',
        question: '¿Qué datos son obligatorios?',
        answer: 'Como base operativa y administrativa, cargá razón social, tipo y número de documento, condición IVA y un criterio mínimo de contacto o domicilio útil.',
        keywords: ['obligatorios', 'razón social', 'cuit', 'iva'],
      }),
      buildQuestion({
        id: 'clients-eventual',
        question: '¿Qué pasa si es eventual?',
        answer: 'Se puede operar con menor configuración comercial, pero igual necesitás datos identificatorios confiables. No lo uses como atajo para omitir validaciones básicas.',
        keywords: ['eventual', 'cliente eventual', 'alta rápida'],
      }),
      buildQuestion({
        id: 'clients-branch',
        question: '¿Qué es sucursal responsable?',
        answer: 'Es la referencia interna que atiende y sigue al cliente. Ayuda a ordenar gestión, coordinación y ownership operativo.',
        keywords: ['sucursal', 'responsable', 'operación'],
      }),
      buildQuestion({
        id: 'clients-duplicates',
        question: '¿Cómo evitar duplicados?',
        answer: 'Buscá antes por CUIT, DNI, razón social y nombre de fantasía. Si encontrás similitudes, revisá si corresponde editar un registro existente en lugar de crear otro.',
        keywords: ['duplicados', 'buscar', 'cuit', 'dni'],
      }),
    ],
  },
  tariffs: {
    id: 'tariffs',
    title: 'Ayuda de tarifas',
    tone: 'Workbench tarifario',
    summary: 'El módulo de tarifas define listas, versiones, conceptos y reglas que luego usa la valorización operativa.',
    intro: 'Conviene pensar el circuito en orden: primero conceptos, después numeradores, luego listas/versiones y por último reglas con prioridad y vigencia coherentes.',
    sections: [
      buildSection({
        id: 'listas-versiones',
        title: 'Lista de precios, versión y vigencia',
        icon: 'ListOrdered',
        objective: 'Organizá el contexto comercial donde viven las reglas.',
        importantFields: [
          'Lista de precios: agrupa el esquema comercial principal.',
          'Versión: congela una configuración en un momento o vigencia determinada.',
          'Vigencia: define desde cuándo y hasta cuándo puede aplicar.',
        ],
        commonErrors: [
          'Editar sobre una versión vigente sin criterio de trazabilidad.',
          'Abrir una nueva versión sin cerrar o revisar la vigencia anterior.',
        ],
        recommendations: [
          'Usá una nueva versión cuando cambie lógica comercial relevante.',
          'Revisá que la vigencia no solape versiones activas incompatibles.',
        ],
        faqs: [
          '¿Qué es una lista de precios?',
          '¿Qué es una versión?',
        ],
        keywords: ['lista', 'versión', 'vigencia', 'lista de precios'],
      }),
      buildSection({
        id: 'conceptos-numeradores',
        title: 'Conceptos y numeradores',
        icon: 'Hash',
        objective: 'Prepará los elementos base que después usan las reglas.',
        importantFields: [
          'Conceptos: representan cargos o componentes valorizables.',
          'Numeradores: ayudan a ordenar, segmentar o identificar configuraciones.',
        ],
        commonErrors: [
          'Crear conceptos duplicados con nombres distintos para el mismo fin.',
          'Usar numeradores sin criterio y perder legibilidad comercial.',
        ],
        recommendations: [
          'Mantené nombres de conceptos claros y reutilizables.',
          'Definí numeradores consistentes antes de escalar reglas.',
        ],
        faqs: [
          '¿Qué son los conceptos?',
          '¿Para qué sirven los numeradores?',
        ],
        keywords: ['conceptos', 'numeradores', 'segmentación', 'catálogo'],
      }),
      buildSection({
        id: 'reglas-prioridad',
        title: 'Reglas, prioridad y alcance',
        icon: 'Workflow',
        objective: 'Determiná cuándo aplica cada cálculo y cuál gana frente a otros.',
        importantFields: [
          'Prioridad: define desempate cuando varias reglas podrían aplicar.',
          'Alcance: puede ser general, por cliente, por servicio o por combinación de condiciones.',
          'Tarifa general vs cliente vs servicio: una regla más específica suele requerir prioridad y vigencia bien controladas.',
        ],
        commonErrors: [
          'Cargar varias reglas equivalentes con distinta prioridad sin criterio claro.',
          'Olvidar contexto de cliente o servicio y esperar que aplique una tarifa específica.',
        ],
        recommendations: [
          'Documentá por qué una regla es más específica que otra.',
          'Simulá antes de publicar para entender por qué gana una regla.',
        ],
        faqs: [
          '¿Qué regla tiene prioridad?',
          '¿Por qué una tarifa puede no aplicar?',
        ],
        keywords: ['reglas', 'prioridad', 'alcance', 'cliente', 'servicio'],
      }),
      buildSection({
        id: 'calculo',
        title: 'Mínimos, porcentajes, valor declarado y adicionales',
        icon: 'Percent',
        objective: 'Ajustá la fórmula de cálculo de manera consistente con el negocio.',
        importantFields: [
          'Mínimos: aseguran piso comercial aun cuando la base calculada sea baja.',
          'Porcentajes: se usan cuando el cálculo depende de una base variable.',
          'Valor declarado y adicionales: aplican solo si la regla comercial los contempla.',
        ],
        commonErrors: [
          'Configurar porcentaje sin revisar base de cálculo.',
          'Esperar que el valor declarado impacte sin una regla que lo contemple.',
        ],
        recommendations: [
          'Probá escenarios límite en el simulador antes de liberar cambios.',
          'No mezcles mínimos y adicionales sin validar el resultado esperado.',
        ],
        faqs: [
          '¿Cuándo usar mínimos?',
          '¿Cómo influye el valor declarado?',
        ],
        shortcuts: ['F1 abre esta ayuda.', 'Escape cierra el panel de ayuda.'],
        keywords: ['mínimos', 'porcentajes', 'valor declarado', 'adicionales', 'simulador'],
      }),
    ],
    assistantQuestions: [
      buildQuestion({
        id: 'tariffs-list',
        question: '¿Qué es una lista de precios?',
        answer: 'Es el contenedor comercial principal. Dentro de esa lista viven una o más versiones y, sobre ellas, las reglas que terminan valorizando la operación.',
        keywords: ['lista', 'lista de precios'],
      }),
      buildQuestion({
        id: 'tariffs-version',
        question: '¿Qué es una versión?',
        answer: 'Es una foto controlada de la tarifa para una vigencia determinada. Sirve para cambiar condiciones sin perder trazabilidad de lo anterior.',
        keywords: ['versión', 'vigencia', 'historial'],
      }),
      buildQuestion({
        id: 'tariffs-concepts',
        question: '¿Qué son los conceptos?',
        answer: 'Son los cargos o componentes valorizables que después aparecen en el cálculo. Ejemplo: flete, seguro, adicional o recargo.',
        keywords: ['conceptos', 'cargo', 'flete'],
      }),
      buildQuestion({
        id: 'tariffs-numerators',
        question: '¿Para qué sirven los numeradores?',
        answer: 'Ayudan a ordenar e identificar configuraciones tarifarias. Son útiles para segmentación, legibilidad y consistencia del esquema.',
        keywords: ['numeradores', 'identificación', 'segmentación'],
      }),
      buildQuestion({
        id: 'tariffs-noapply',
        question: '¿Por qué una tarifa puede no aplicar?',
        answer: 'Porque no coincide cliente, servicio, origen, destino, vigencia, prioridad o base de cálculo. El simulador ayuda a detectar cuál condición dejó afuera la regla.',
        keywords: ['no aplica', 'vigencia', 'prioridad', 'simulador'],
      }),
      buildQuestion({
        id: 'tariffs-priority',
        question: '¿Qué regla tiene prioridad?',
        answer: 'La que combine mejor especificidad y prioridad configurada dentro de la vigencia válida. Si dos reglas compiten, revisá prioridad, alcance y solapamientos.',
        keywords: ['prioridad', 'regla', 'solapamiento'],
      }),
    ],
  },
  generic: {
    id: 'generic',
    title: 'Ayuda contextual',
    tone: 'Asistencia local',
    summary: 'Esta pantalla todavía no tiene una guía específica, pero el sistema ya está listo para sumarla sin tocar la lógica operativa.',
    intro: 'Usá F1 para abrir ayuda según la ruta actual. Cuando se registre una pantalla nueva, va a aparecer acá automáticamente.',
    sections: [
      buildSection({
        id: 'generic-overview',
        title: 'Cómo usar la ayuda',
        icon: 'CircleHelp',
        objective: 'Abrí la guía, revisá recomendaciones y usá el asistente con preguntas frecuentes.',
        importantFields: [
          'F1 abre la ayuda contextual de la pantalla actual.',
          'Escape cierra el panel sin interrumpir tu trabajo.',
        ],
        commonErrors: [],
        recommendations: [
          'Si esta vista necesita ayuda específica, se puede agregar en el registro local sin backend ni IA.',
        ],
        faqs: ['¿Por qué esta pantalla no tiene ayuda específica todavía?'],
        shortcuts: ['F1 abre ayuda.', 'Escape la cierra.'],
        keywords: ['ayuda', 'f1', 'escape', 'contextual'],
      }),
    ],
    assistantQuestions: [
      buildQuestion({
        id: 'generic-availability',
        question: '¿Por qué esta pantalla no tiene ayuda específica todavía?',
        answer: 'Porque la capa de ayuda es incremental. La infraestructura ya está lista y se puede registrar contenido local nuevo sin tocar negocio ni servicios.',
        keywords: ['pantalla', 'ayuda específica', 'registro'],
      }),
    ],
  },
};

export default HELP_CONTENT;
